import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Slider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Pagination from '@mui/lab/Pagination';
import _ from 'lodash';
import SearchInput from '../../components/SearchInput';
import ActionButton from '../../components/ActionButton';
import EmptyState from '../../components/EmptyState';
import CustomShowModal from '../Common/customShowModal';
import { toastError } from '../../utils';
import * as actions from './actions';
import { ExternalLink, Music, RefreshCw, Copy, Check, Clock, Calendar, Download, Play, Pause, Volume2, Headphones } from 'lucide-react';

const serviceColors = {
  spotify: '#1DB954',
  appleMusic: '#FC3C44',
  youtubeMusic: '#FF0000',
  youtube: '#FF0000',
  amazonMusic: '#00A8E1',
  deezer: '#A238FF',
  tidal: '#000000',
  soundcloud: '#FF5500',
  pandora: '#224099',
  iHeartRadio: '#C6002B',
  bandcamp: '#629AA9',
  napster: '#007AFF',
  jioSaavn: '#2BC5B4',
  gaana: '#E72C30',
  tiktokMusic: '#FE2C55',
  instagram: '#E1306C',
  qqMusic: '#31C27C',
  netease: '#C20C0C',
  yandex: '#FFCC00',
  audius: '#CC0FE0',
  lineMusic: '#00B900',
  awa: '#FC6631',
  kkbox: '#09CEF6',
  wynkMusic: '#1D9CEF',
  qobuz: '#296FA8',
  sevenDigital: '#E22A2A',
  amazon: '#FF9900',
  shazam: '#0088FF',
};

function normalizeServiceName(str) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const serviceKeyToLogoName = {
  amazonMusic: 'amazon',
  tiktokMusic: 'tiktok',
  instagram: 'facebook,instagram',
  netease: 'neteasecloudmusic',
  sevenDigital: '7digital',
  youtube: 'youtubemusic',
  youtubeShorts: 'youtubemusic',
};

function getLogoUrl(serviceKey, logosMap) {
  const override = serviceKeyToLogoName[serviceKey];
  if (override) return logosMap[normalizeServiceName(override)];
  return logosMap[normalizeServiceName(serviceKey)];
}

function formatServiceName(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuration(ms) {
  if (!ms) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function buildLinksText(data) {
  let text = `Title: ${data.track_name || 'Unknown Track'}\n\n`;
  const services = data.services || {};
  for (const [name, service] of Object.entries(services)) {
    if (service && service.link) {
      text += `${formatServiceName(name)}: ${service.link}\n`;
    }
  }
  text += `\nGenerated by Fronic Media\nhttps://fronicmedia.com/`;
  return text;
}

function downloadAsTextFile(text, songName) {
  const sanitized = (songName || 'links').replace(/[<>:"/\\|?*]/g, '_').trim() || 'links';
  const blob = new Blob([text], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${sanitized}.txt`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function formatTime(seconds) {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

const SeekSlider = styled(Slider)({
  color: '#6C63FF',
  height: 4,
  padding: '8px 0',
  '& .MuiSlider-track': {
    background: 'linear-gradient(90deg, #6C63FF, #A78BFA)',
    border: 'none',
  },
  '& .MuiSlider-rail': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  '& .MuiSlider-thumb': {
    width: 14,
    height: 14,
    background: 'linear-gradient(135deg, #6C63FF, #A78BFA)',
    boxShadow: '0 0 8px rgba(108,99,255,0.5)',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 12px rgba(108,99,255,0.7)',
    },
  },
});

const ServiceCard = styled(Box)({
  background: 'var(--bg-elevated, #232636)',
  borderRadius: 12,
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  transition: 'all 0.25s ease',
  cursor: 'pointer',
  textDecoration: 'none',
  border: '1px solid var(--border, #2E3247)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
    border: '1px solid rgba(108, 99, 255, 0.3)',
  },
});

export default function Musilinks() {
  const [isrc, setIsrc] = useState('');
  const [fetching, setFetching] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');
  const [copiedAll, setCopiedAll] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [filters, setFilters] = useState({ limit: 100, offset: 0, page: 1, search: '' });
  const [historyLoading, setHistoryLoading] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Logos state
  const [logos, setLogos] = useState({});

  // Audio player state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  useEffect(() => {
    document.title = 'MU | Musilinks';
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  useEffect(() => {
    loadHistory(filters);
  }, []);

  useEffect(() => {
    actions.getLogos().then((res) => {
      if (res.status === 200 && Array.isArray(res.body)) {
        const map = {};
        res.body.forEach((item) => {
          if (item.name && item.logo) {
            map[normalizeServiceName(item.name)] = item.logo;
          }
        });
        setLogos(map);
      }
    }).catch(() => {});
  }, []);

  const loadHistory = (f) => {
    setHistoryLoading(true);
    actions.getMusilinksHistoryAdmin({
      limit: f.limit,
      offset: f.offset,
      search: f.search,
    })
      .then((res) => {
        setHistoryLoading(false);
        if (res.status === 200) {
          setHistory(res.body.rows || []);
          setHistoryTotal(res.body.total || 0);
        }
      })
      .catch(() => setHistoryLoading(false));
  };

  const totalPages = Math.ceil(Number(historyTotal) / filters.limit);

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    loadHistory(newFilters);
  };

  const delaySearch = useCallback(
    _.debounce((f) => loadHistory(f), 500),
    []
  );

  const onChangeHistorySearch = (e) => {
    const val = e.target.value;
    const newFilters = { ...filters, search: val, page: 1, offset: 0 };
    setFilters(newFilters);
    if (val.trim().length >= 3 || val.trim().length === 0) {
      delaySearch(newFilters);
    }
  };

  const handleFetch = () => {
    if (!isrc.trim()) return;
    setFetching(true);
    setModalOpen(true);
    setModalData(null);
    setModalLoading(true);
    actions.fetchMusicLinksAdmin({ input: isrc.trim() })
      .then((res) => {
        setFetching(false);
        setModalLoading(false);
        if (res.status === 200) {
          setModalData({ ...res.body, isrc: isrc.trim() });
          loadHistory(filters);
        } else {
          setModalOpen(false);
          toastError(res?.message || 'Links not available');
        }
      })
      .catch((err) => {
        setFetching(false);
        setModalLoading(false);
        setModalOpen(false);
        toastError(err?.message || 'Links not available');
      });
  };

  const handleRefetch = () => {
    if (!modalData?.isrc) return;
    setModalLoading(true);
    actions.fetchMusicLinksAdmin({ input: modalData.isrc })
      .then((res) => {
        setModalLoading(false);
        if (res.status === 200) {
          setModalData({ ...res.body, isrc: modalData.isrc });
          loadHistory(filters);
        }
      })
      .catch(() => setModalLoading(false));
  };

  const handleIsrcKeyDown = (e) => {
    if (e.key === 'Enter') handleFetch();
  };

  const handleHistoryCardClick = (row) => {
    if (!row.isrc) return;
    setModalOpen(true);
    setModalData(null);
    setModalLoading(true);
    actions.getMusicLinksAdmin({ isrc: row.isrc })
      .then((res) => {
        setModalLoading(false);
        if (res.status === 200 && res.body.cached) {
          setModalData({ ...res.body, isrc: row.isrc });
        } else {
          setModalData({ empty: true, isrc: row.isrc, track_name: row.track_name });
        }
      })
      .catch(() => {
        setModalLoading(false);
        setModalData({ empty: true, isrc: row.isrc, track_name: row.track_name });
      });
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (_, value) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const handleCloseModal = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setAudioDuration(0);
    setModalOpen(false);
    setModalData(null);
    setCopiedLink('');
    setCopiedAll(false);
  };

  const handleExport = () => {
    if (!modalData) return;
    const text = buildLinksText(modalData);
    downloadAsTextFile(text, modalData.track_name);
  };

  const handleCopyAll = () => {
    if (!modalData) return;
    const text = buildLinksText(modalData);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    });
  };

  const renderServiceGrid = () => {
    if (!modalData || modalData.empty) return null;
    const services = modalData.services || {};
    const serviceEntries = Object.entries(services).filter(([, s]) => s && s.link);
    if (serviceEntries.length === 0) {
      return (
        <Typography sx={{ color: '#64748B', fontSize: '0.85rem', textAlign: 'center', py: 2 }}>
          No streaming links available.
        </Typography>
      );
    }
    return (
      <Grid container spacing={1.5}>
        {serviceEntries.map(([name, service]) => {
          const color = serviceColors[name] || '#6C63FF';
          return (
            <Grid item xs={12} sm={6} key={name}>
              <ServiceCard>
                <Box sx={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: 3, borderRadius: '3px 0 0 3px', background: color,
                }} />
                {(() => {
                  const logoUrl = getLogoUrl(name, logos);
                  if (logoUrl) {
                    return (
                      <Box
                        component="img"
                        src={logoUrl}
                        alt={formatServiceName(name)}
                        sx={{
                          width: 36, height: 36, borderRadius: '8px', flexShrink: 0, ml: 0.5,
                          objectFit: 'contain',
                        }}
                      />
                    );
                  }
                  return (
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '8px', flexShrink: 0, ml: 0.5,
                      backgroundColor: `${color}20`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem', color: color, fontWeight: 700,
                    }}>
                      {formatServiceName(name).charAt(0)}
                    </Box>
                  );
                })()}
                <Typography sx={{
                  color: '#F1F5F9', fontWeight: 600,
                  fontSize: '0.8rem', lineHeight: 1.3,
                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {formatServiceName(name)}
                </Typography>
                <Tooltip title={copiedLink === name ? 'Copied!' : 'Copy link'} arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(service.link);
                      setCopiedLink(name);
                      setTimeout(() => setCopiedLink(''), 2000);
                    }}
                    sx={{
                      flexShrink: 0, p: 0.5,
                      color: copiedLink === name ? '#10B981' : '#64748B',
                      '&:hover': { color: copiedLink === name ? '#10B981' : '#6C63FF' },
                    }}
                  >
                    {copiedLink === name ? <Check size={14} /> : <Copy size={14} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in new tab" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(service.link, '_blank', 'noopener,noreferrer');
                    }}
                    sx={{
                      flexShrink: 0, p: 0.5,
                      color: '#64748B',
                      '&:hover': { color: '#6C63FF' },
                    }}
                  >
                    <ExternalLink size={14} />
                  </IconButton>
                </Tooltip>
              </ServiceCard>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderModalContent = () => {
    if (modalLoading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
          <CircularProgress size={36} sx={{ color: '#6C63FF', mb: 2 }} />
          <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
            Fetching streaming links...
          </Typography>
        </Box>
      );
    }

    if (modalData && modalData.empty) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
            background: 'rgba(108, 99, 255, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Music size={28} color="#6C63FF" />
          </Box>
          <Typography sx={{ color: '#F1F5F9', fontSize: '0.95rem', fontWeight: 600, mb: 0.5 }}>
            No cached links found
          </Typography>
          <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>
            Try fetching links for this ISRC using the search bar.
          </Typography>
        </Box>
      );
    }

    if (!modalData) return null;

    const services = modalData.services || {};
    const serviceEntries = Object.entries(services).filter(([, s]) => s && s.link);
    const duration = formatDuration(modalData.track_duration);

    return (
      <Box>
        {/* Track hero */}
        <Box sx={{
          display: 'flex', gap: 2.5, mb: 3, p: 2.5,
          background: 'var(--bg-elevated, #232636)',
          borderRadius: 3,
          border: '1px solid var(--border, #2E3247)',
        }}>
          {modalData.track_image ? (
            <Box
              component="img"
              src={modalData.track_image}
              alt={modalData.track_name}
              referrerPolicy="no-referrer"
              sx={{
                width: 96, height: 96,
                borderRadius: 2,
                objectFit: 'cover',
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                flexShrink: 0,
              }}
            />
          ) : (
            <Box sx={{
              width: 96, height: 96, borderRadius: 2, flexShrink: 0,
              background: 'linear-gradient(135deg, #6C63FF 0%, #00bfa5 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Music size={36} color="#fff" />
            </Box>
          )}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
            <Typography sx={{
              fontWeight: 700, fontSize: '1.1rem', color: '#F1F5F9',
              lineHeight: 1.3, mb: 0.5,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {modalData.track_name || 'Unknown Track'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              {modalData.isrc && (
                <Chip
                  label={modalData.isrc}
                  size="small"
                  sx={{
                    fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 600,
                    background: 'rgba(167, 139, 250, 0.12)', color: '#A78BFA',
                    border: '1px solid rgba(167, 139, 250, 0.2)',
                    height: 22,
                  }}
                />
              )}
              <Chip
                label={`${serviceEntries.length} platform${serviceEntries.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  background: 'rgba(108, 99, 255, 0.15)', color: '#8B8AFF',
                  fontWeight: 600, fontSize: '0.72rem', height: 22,
                }}
              />
              {duration && (
                <Chip
                  icon={<Clock size={11} />}
                  label={duration}
                  size="small"
                  sx={{
                    background: 'rgba(100, 116, 139, 0.15)', color: '#94A3B8',
                    fontSize: '0.72rem', height: 22,
                    '& .MuiChip-icon': { color: '#94A3B8' },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              {modalData.init_by && (
                <Chip
                  label={`By ${modalData.init_by === 'admin' ? 'Admin' : 'User'}`}
                  size="small"
                  sx={{
                    height: 20,
                    background: modalData.init_by === 'admin' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                    color: modalData.init_by === 'admin' ? '#3B82F6' : '#10B981',
                    fontWeight: 500, fontSize: '0.68rem',
                  }}
                />
              )}
              {modalData.fetch_count != null && (
                <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                  Fetched {modalData.fetch_count} time{modalData.fetch_count !== 1 ? 's' : ''} by users
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, ml: 'auto' }}>
                <Box
                  onClick={handleExport}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    border: '1px solid rgba(0, 188, 212, 0.3)',
                    borderRadius: '8px',
                    color: '#00BCD4',
                    px: 1.2, py: 0.4,
                    fontSize: '0.7rem', fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': { background: 'rgba(0, 188, 212, 0.12)', border: '1px solid rgba(0, 188, 212, 0.5)' },
                  }}
                >
                  <Download size={12} /> Export
                </Box>
                <Box
                  onClick={handleCopyAll}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    border: copiedAll ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(167, 139, 250, 0.3)',
                    borderRadius: '8px',
                    color: copiedAll ? '#10B981' : '#A78BFA',
                    px: 1.2, py: 0.4,
                    fontSize: '0.7rem', fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: copiedAll ? 'rgba(16, 185, 129, 0.12)' : 'rgba(167, 139, 250, 0.12)',
                      border: copiedAll ? '1px solid rgba(16, 185, 129, 0.5)' : '1px solid rgba(167, 139, 250, 0.5)',
                    },
                  }}
                >
                  {copiedAll ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy All</>}
                </Box>
                <ActionButton variant="info" onClick={handleRefetch} sx={{ fontSize: '0.72rem', px: 1.5, py: 0.3, gap: 0.5 }}>
                  <RefreshCw size={12} /> Refetch
                </ActionButton>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Preview player */}
        {(() => {
          const previewSrc = modalData.preview_url
            ? (Array.isArray(modalData.preview_url) ? modalData.preview_url[0] : modalData.preview_url)
            : null;
          if (!previewSrc) return null;
          return (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <Headphones size={14} color="#6C63FF" />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Preview
                </Typography>
              </Box>
              <audio
                ref={audioRef}
                src={previewSrc}
                onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
                onLoadedMetadata={() => setAudioDuration(audioRef.current?.duration || 0)}
                onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
                style={{ display: 'none' }}
              />
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                background: 'var(--bg-elevated, #232636)',
                borderRadius: 3,
                padding: '8px 14px',
                border: '1px solid var(--border, #2E3247)',
              }}>
                <IconButton
                  onClick={togglePlay}
                  sx={{
                    width: 36, height: 36,
                    background: 'linear-gradient(135deg, #6C63FF, #A78BFA)',
                    color: '#fff',
                    boxShadow: '0 0 12px rgba(108,99,255,0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5B54E6, #9575CD)',
                      boxShadow: '0 0 18px rgba(108,99,255,0.6)',
                    },
                  }}
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: 2 }} />}
                </IconButton>
                <Typography sx={{
                  color: 'rgba(226,232,240,0.7)',
                  fontFamily: 'monospace',
                  fontSize: '0.78rem',
                  minWidth: 70,
                  textAlign: 'center',
                  userSelect: 'none',
                }}>
                  {formatTime(currentTime)} / {formatTime(audioDuration)}
                </Typography>
                <SeekSlider
                  value={currentTime}
                  max={audioDuration || 1}
                  onChange={handleSeek}
                  sx={{ flex: 1 }}
                />
                <Volume2 size={16} color="rgba(108,99,255,0.5)" />
              </Box>
            </Box>
          );
        })()}

        {/* Section label */}
        <Typography sx={{
          fontSize: '0.72rem', fontWeight: 700, color: '#64748B',
          textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5,
        }}>
          Available on
        </Typography>

        {/* Service grid */}
        {renderServiceGrid()}
      </Box>
    );
  };

  const renderHistoryCard = (row, index) => (
    <Paper
      key={row.id || index}
      className="approved-release-card"
      onClick={() => handleHistoryCardClick(row)}
    >
      <div className="approved-release-card__artwork">
        {row.track_image ? (
          <img src={row.track_image} alt={row.track_name} referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
        ) : (
          <div className="approved-release-card__artwork-fallback">
            <Music size={20} color="#64748B" />
            <span>Link</span>
          </div>
        )}
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {row.track_name || 'Unknown Track'}
            </Typography>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 'auto', justifyContent: 'flex-end' }}>
            <Chip
              icon={<ExternalLink size={11} />}
              label={`${row.platform_count || 0} platforms`}
              size="small"
              sx={{
                backgroundColor: 'rgba(108, 99, 255, 0.12)',
                color: '#A78BFA',
                border: '1px solid rgba(108, 99, 255, 0.25)',
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 22,
                '& .MuiChip-icon': { color: '#A78BFA' },
              }}
            />
            <span style={{ color: 'var(--border)', margin: '0 2px' }}>|</span>
            <Calendar size={14} style={{ color: '#F59E0B' }} />
            <span style={{ color: '#F59E0B', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace' }}>
              {formatDate(row.updated_at)}
            </span>
          </span>
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id" style={{ fontFamily: 'monospace' }}>{row.isrc || '-'}</span>
          <span className="approved-release-card__meta-item">
            <Clock size={13} /> Fetches: {row.fetch_count || 0}
          </span>
          <Chip
            label={row.init_by ? `Searched by ${row.init_by === 'admin' ? 'Admin' : 'User'}` : '-'}
            size="small"
            sx={{
              height: 20,
              backgroundColor: row.init_by === 'admin' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(16, 185, 129, 0.15)',
              color: row.init_by === 'admin' ? '#3B82F6' : '#10B981',
              fontSize: '0.7rem',
              fontWeight: 500,
            }}
          />
        </div>
      </div>

      <div className="approved-release-card__arrow">
        <ExternalLink size={16} />
      </div>
    </Paper>
  );

  const modalTitle = modalData && !modalData.empty
    ? `Streaming Links — ${modalData.track_name || modalData.isrc || ''}`
    : 'Streaming Links';

  return (
    <>
      <Grid container>
        {/* Search Section */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1.5} mb={3}>
            <Box sx={{ maxWidth: 420, flex: 1 }}>
              <SearchInput
                value={isrc}
                onChange={(e) => setIsrc(e.target.value)}
                placeholder="Enter ISRC or URL..."
                onKeyDown={handleIsrcKeyDown}
              />
            </Box>
            <ActionButton variant="success" onClick={handleFetch} disabled={!isrc.trim() || fetching}>
              {fetching ? 'Fetching...' : 'Fetch Links'}
            </ActionButton>
          </Box>
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider sx={{ borderColor: 'var(--border)', mb: 3 }} />
        </Grid>

        {/* ========== History ========== */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
            <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
              History
            </Typography>
            <Chip
              icon={<ExternalLink size={14} />}
              label={`${historyTotal} Total`}
              sx={{
                backgroundColor: 'rgba(108, 99, 255, 0.12)',
                color: '#6C63FF',
                border: '1px solid rgba(108, 99, 255, 0.25)',
                fontWeight: 600,
                fontSize: '0.85rem',
                height: 34,
                flexShrink: 0,
                '& .MuiChip-icon': { color: '#6C63FF' },
              }}
            />
          </Box>
          <Box mb={3} sx={{ maxWidth: 420 }}>
            <SearchInput
              value={filters.search}
              onChange={onChangeHistorySearch}
              placeholder="Search by ISRC or track name..."
            />
          </Box>
          <Box className="approved-release-list">
            {historyLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={30} sx={{ color: '#6C63FF' }} />
              </Box>
            ) : history.length ? (
              history.map((row, index) => renderHistoryCard(row, index))
            ) : (
              <Paper sx={{ p: 0 }}>
                <table style={{ width: '100%' }}><tbody>
                  <EmptyState message="No History Found" colSpan={1} />
                </tbody></table>
              </Paper>
            )}
          </Box>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                showFirstButton
                showLastButton
                onChange={onPageChange}
                page={filters.page || 1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#94A3B8',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(108, 99, 255, 0.2)',
                      color: '#6C63FF',
                    },
                  },
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Streaming Links Modal */}
      <CustomShowModal
        open={modalOpen}
        handleClose={handleCloseModal}
        title={modalTitle}
        maxWidth="md"
      >
        {renderModalContent()}
      </CustomShowModal>
    </>
  );
}
